/*
 * @license
 * Copyright Endlessjs. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import {
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    Directive,
    EventEmitter,
    HostBinding,
    HostListener,
    Inject,
    Input,
    Output,
    TemplateRef,
} from '@angular/core';

import { convertToBoolProperty } from '../helpers';
import { EL_SORT_HEADER_COLUMN_DEF } from '../cdk/table/cell';

/** Column definition associated with a `ElSortHeaderDirective`. */
interface ElSortHeaderColumnDef {
    name: string;
}

export interface ElSortRequest {
    column: string;
    direction: ElSortDirection;
}

export interface ElSortable {
    sort(sortRequest: ElSortRequest);
}

export enum ElSortDirection {
    ASCENDING = 'asc',
    DESCENDING = 'desc',
    NONE = '',
}
const sortDirections: ElSortDirection[] = [
    ElSortDirection.ASCENDING,
    ElSortDirection.DESCENDING,
    ElSortDirection.NONE,
];

/**
 * Directive triggers sort method of passed object when sort header changes direction
 */
@Directive({ selector: '[elSort]' })
export class ElSortDirective {
    @Input('elSort') sortable: ElSortable;

    @Output() sort: EventEmitter<ElSortRequest> = new EventEmitter<ElSortRequest>();

    emitSort(sortRequest: ElSortRequest) {
        if (this.sortable && this.sortable.sort) {
            this.sortable.sort(sortRequest);
        }
        this.sort.emit(sortRequest);
    }
}

export interface ElSortHeaderIconDirectiveContext {
    $implicit: ElSortDirection;
    isAscending: boolean;
    isDescending: boolean;
    isNone: boolean;
}

/**
 * Directive for headers sort icons. Mark you icon implementation with this structural directive and
 * it'll set template's implicit context with current direction. Context also has `isAscending`,
 * `isDescending` and `isNone` properties.
 */
@Directive({ selector: '[elSortHeaderIcon]' })
export class ElSortHeaderIconDirective { }

@Component({
    selector: 'el-sort-icon',
    template: `
    <ng-container *ngIf="isDirectionSet()">
      <el-icon *ngIf="isAscending()" icon="chevron-down-outline" pack="endlessjs-essentials" aria-hidden="true"></el-icon>
      <el-icon *ngIf="isDescending()" icon="chevron-up-outline" pack="endlessjs-essentials" aria-hidden="true"></el-icon>
    </ng-container>
  `,
})
export class ElSortIconComponent {
    @Input() direction: ElSortDirection = ElSortDirection.NONE;

    isAscending(): boolean {
        return this.direction === ElSortDirection.ASCENDING;
    }

    isDescending(): boolean {
        return this.direction === ElSortDirection.DESCENDING;
    }

    isDirectionSet(): boolean {
        return this.isAscending() || this.isDescending();
    }
}

/**
 * Marks header as sort header so it emitting sort event when clicked.
 */
@Component({
    selector: '[elSortHeader]',
    template: `
    <button
      class="el-tree-grid-header-change-sort-button"
      type="button"
      [attr.disabled]="getDisabledAttributeValue()"
      (click)="sortData()">
      <ng-content></ng-content>
    </button>
    <el-sort-icon *ngIf="!sortIcon; else customIcon" [direction]="direction"></el-sort-icon>
    <ng-template #customIcon [ngTemplateOutlet]="sortIcon" [ngTemplateOutletContext]="getIconContext()"></ng-template>
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElSortHeaderComponent {

    @ContentChild(ElSortHeaderIconDirective, { read: TemplateRef, static: false })
    sortIcon: TemplateRef<ElSortHeaderIconDirectiveContext>;

    /**
     * Current sort direction. Possible values: `asc`, `desc`, ``(none)
     * @type {ElSortDirection}
     */
    @Input('elSortHeader') direction: ElSortDirection;

    private disabledValue: boolean = false;

    /**
     * Disable sort header
     */
    @Input()
    @HostBinding('class.disabled')
    set disabled(value) {
        this.disabledValue = convertToBoolProperty(value);
    }
    get disabled(): boolean {
        return this.disabledValue;
    }

    @HostListener('click')
    sortIfEnabled() {
        if (!this.disabled) {
            this.sortData();
        }
    }

    constructor(
        private sort: ElSortDirective,
        @Inject(EL_SORT_HEADER_COLUMN_DEF) private columnDef: ElSortHeaderColumnDef,
    ) { }

    isAscending(): boolean {
        return this.direction === ElSortDirection.ASCENDING;
    }

    isDescending(): boolean {
        return this.direction === ElSortDirection.DESCENDING;
    }

    sortData(): void {
        const sortRequest = this.createSortRequest();
        this.sort.emitSort(sortRequest);
    }

    getIconContext(): ElSortHeaderIconDirectiveContext {
        return {
            $implicit: this.direction,
            isAscending: this.isAscending(),
            isDescending: this.isDescending(),
            isNone: !this.isAscending() && !this.isDescending(),
        };
    }

    getDisabledAttributeValue(): '' | null {
        return this.disabled ? '' : null;
    }

    private createSortRequest(): ElSortRequest {
        this.direction = this.getNextDirection();
        return { direction: this.direction, column: this.columnDef.name };
    }

    private getNextDirection(): ElSortDirection {
        const sortDirectionCycle = sortDirections;
        let nextDirectionIndex = sortDirectionCycle.indexOf(this.direction) + 1;
        if (nextDirectionIndex >= sortDirectionCycle.length) {
            nextDirectionIndex = 0;
        }
        return sortDirectionCycle[nextDirectionIndex];
    }
}
